import React from 'react';
import { AppProvider, useApp } from './context/AppContext';
import { CRMModule } from './components/CRMModule';
import { SchedulingModule } from './components/SchedulingModule';
import { DeliveriesModule } from './components/DeliveriesModule';
import { BuildersModule } from './components/BuildersModule';
import { ContactsModule } from './components/ContactsModule';
import { Toaster } from './components/ui/sonner';
import { 
  Building2, 
  Calendar, 
  Truck,
  HardHat,
  Contact,
  FolderKanban
} from 'lucide-react';
import { cn } from './lib/utils';
import './App.css';

function AppContent() {
  const { state, actions } = useApp();
  const { activeModule } = state;

  return (
    <div className="h-screen flex bg-background" data-testid="app-root">
      {/* Sidebar Navigation */}
      <nav className="w-16 bg-slate-900 flex flex-col items-center py-4 shrink-0" data-testid="sidebar-nav">
        <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center mb-8">
          <Building2 className="w-6 h-6 text-primary-foreground" />
        </div>
        
        <div className="flex flex-col gap-2 flex-1">
          <NavButton
            icon={<FolderKanban className="w-5 h-5" />}
            label="CRM"
            active={activeModule === 'crm'}
            onClick={() => actions.setActiveModule('crm')}
            testId="nav-crm"
          />
          <NavButton
            icon={<HardHat className="w-5 h-5" />}
            label="Builders"
            active={activeModule === 'builders'}
            onClick={() => actions.setActiveModule('builders')}
            testId="nav-builders"
          />
          <NavButton
            icon={<Contact className="w-5 h-5" />}
            label="Contacts"
            active={activeModule === 'contacts'}
            onClick={() => actions.setActiveModule('contacts')}
            testId="nav-contacts"
          />
          <NavButton
            icon={<Calendar className="w-5 h-5" />}
            label="Schedule"
            active={activeModule === 'scheduling'}
            onClick={() => actions.setActiveModule('scheduling')}
            testId="nav-scheduling"
          />
          <NavButton
            icon={<Truck className="w-5 h-5" />}
            label="Deliveries"
            active={activeModule === 'deliveries'}
            onClick={() => actions.setActiveModule('deliveries')}
            testId="nav-deliveries"
          />
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-1 overflow-hidden">
        {activeModule === 'crm' && <CRMModule />}
        {activeModule === 'builders' && <BuildersModule />}
        {activeModule === 'contacts' && <ContactsModule />}
        {activeModule === 'scheduling' && <SchedulingModule />}
        {activeModule === 'deliveries' && <DeliveriesModule />}
      </main>
      
      <Toaster position="bottom-right" />
    </div>
  );
}

function NavButton({ icon, label, active, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "w-12 h-12 rounded-lg flex flex-col items-center justify-center gap-0.5 transition-all",
        active 
          ? "bg-primary text-primary-foreground" 
          : "text-slate-400 hover:text-white hover:bg-slate-800"
      )}
      title={label}
      data-testid={testId}
    >
      {icon}
      <span className="text-[9px] font-medium">{label}</span>
    </button>
  );
}

function App() {
  return (
    <AppProvider>
      <AppContent />
    </AppProvider>
  );
}

export default App;
